export function SellerFinancingStructures() {
  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Common Seller Financing Structures
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Understanding different financing structures helps you choose the best option for your situation and goals
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-text-line text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Full Seller Financing</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              You finance the entire purchase price (minus down payment) with no bank involvement. Most flexible structure with complete control over terms.
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Maximum flexibility and control</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Highest interest income potential</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Fastest closing process</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-stack-line text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Partial Seller Financing</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Buyer obtains primary financing from a bank, and you provide a second mortgage for a portion. Reduces your risk while still offering benefits.
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Lower risk with smaller loan amount</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Buyer vetted by bank underwriting</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Still earn interest income</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-time-line text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Balloon Payment</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Structure loan with lower monthly payments and a large balloon payment due after 3-10 years. Reduces long-term risk while maintaining cash flow.
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Shorter commitment period</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Buyer refinances or sells to pay balloon</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Steady income until balloon due</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-exchange-line text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Wraparound Mortgage</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              If you have an existing mortgage, buyer makes payments to you, and you continue paying your original mortgage. Creates interest rate spread.
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Profit from interest rate differential</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                <span>No need to pay off existing loan</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Requires due-on-sale clause review</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-contract-line text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Land Contract</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Also called contract for deed. You retain legal title until loan is fully paid. Buyer gets equitable title and possession immediately.
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Maximum seller protection</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Easier to reclaim property if needed</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                <span>State-specific regulations apply</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-hand-coin-line text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Lease Option</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Buyer leases property with option to purchase. Portion of rent credits toward purchase price. Converts to seller financing at purchase.
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Test buyer's commitment first</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Immediate rental income</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Buyer builds equity through rent credits</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Choosing the Right Structure
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <img
                src="https://public.readdy.ai/ai/img_res/a673fb9a8c45909bdec908db83f6c8c0.jpg"
                alt="Seller financing structure options consultation"
                className="w-full h-80 object-cover object-top rounded-lg shadow-lg"
              />
            </div>

            <div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Factors to Consider</h4>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <div>
                    <strong className="text-gray-900">Your Risk Tolerance:</strong>
                    <p className="text-gray-700">How much exposure are you comfortable with?</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <div>
                    <strong className="text-gray-900">Income Needs:</strong>
                    <p className="text-gray-700">Do you need immediate cash or prefer long-term income?</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <div>
                    <strong className="text-gray-900">Buyer Qualifications:</strong>
                    <p className="text-gray-700">How strong is the buyer financially?</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <div>
                    <strong className="text-gray-900">Property Condition:</strong>
                    <p className="text-gray-700">Does the property qualify for traditional financing?</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <div>
                    <strong className="text-gray-900">Market Conditions:</strong>
                    <p className="text-gray-700">Is it a buyer's or seller's market?</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <div>
                    <strong className="text-gray-900">Tax Implications:</strong>
                    <p className="text-gray-700">Which structure optimizes your tax situation?</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}